"use client"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import React, { ComponentProps, ElementRef, forwardRef } from "react"
import {
  TooltipVariantProps,
  tooltipVariants,
} from "../../styles/utils/tooltip"
import { classNames } from "../../utils"

export type PopoverProps = Pick<
  ComponentProps<typeof PopoverPrimitive.Root>,
  "open" | "defaultOpen" | "onOpenChange" | "modal"
> &
  Omit<ComponentProps<typeof PopoverPrimitive.Content>, "content"> &
  TooltipVariantProps & {
    /**
     * Content to render in the portal.
     */
    content: React.ReactNode

    /**
     * Whether to disable the Popover from opening.
     */
    disabled?: boolean

    /**
     * Whether to render the content in a portal.
     */
    portal?: boolean
  }

/**
 * Displays rich content in a portal, triggered by a button.
 */
export const Popover = forwardRef<
  ElementRef<typeof PopoverPrimitive.Content>,
  PopoverProps
>(function Popover(
  {
    open,
    defaultOpen,
    onOpenChange,
    content,
    children,
    className,
    disabled,
    align = "center",
    sideOffset = 4,
    animation = "fade-in",
    portal = true,
    modal,
    ...props
  },
  ref,
) {
  if (disabled || !content) {
    return children
  }

  const popoverContent = (
    <PopoverPrimitive.Content
      align={align}
      className={classNames(
        "overflow-auto",
        "min-w-[var(--radix-popover-trigger-width)]",
        "max-h-[350px] p-2",
        tooltipVariants({ animation }),
        className,
      )}
      ref={ref}
      sideOffset={sideOffset}
      {...props}
    >
      {content}
    </PopoverPrimitive.Content>
  )

  return (
    <PopoverPrimitive.Root
      defaultOpen={defaultOpen}
      modal={modal}
      open={open}
      onOpenChange={onOpenChange}
    >
      <PopoverPrimitive.Trigger asChild>{children}</PopoverPrimitive.Trigger>

      {portal ? (
        <PopoverPrimitive.Portal>{popoverContent}</PopoverPrimitive.Portal>
      ) : (
        popoverContent
      )}
    </PopoverPrimitive.Root>
  )
})
